import { useEffect, useState } from "react";
import { Collapse, Form, Table } from "react-bootstrap";
import { useQuery } from "react-query";
import SetTempEditOrderModal from "../../../components/modals/orderEditTemp";
import OverlayLoader from "../../../components/overlayLoader";
import { SkeletonRows } from "../../../components/skeleton";
import { getServicesReports } from "../../../services/services";
import { onQuerySuccess } from "../../../utils/queryResponse";
import ServicesReportsFilter from "./servicesReportsFilter";
import { rsdFormat } from "../../../utils/helper";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const ServicesReportsPage = () => {

    const [editJobModal, setEditJobModal] = useState(false)

    const [isGroupedByCategory, setIsGroupedByCategory] = useState(false)

    const [reports, setReports] = useState([])
    const [groupedReports, setGroupedReports] = useState([])
    const [selectedJobId, setSelectedJobId] = useState('')

    const [filters, setFilters] = useState(['', '', ''])

    let navigate = useNavigate()
    const { t } = useTranslation();

    const { data, isFetching } = useQuery(
        ['services-reports', ...filters],
        () => getServicesReports(...filters)
    )

    useEffect(() => {
        onQuerySuccess(data, (result) => {

            let _groupedReports = result.reduce((group, report) => {
                const { category } = report;
                group[category] = group[category] ?? [];
                group[category].push(report);
                return group;
            }, {});

            let _groupedReportsArray = []
            for (const [key, value] of Object.entries(_groupedReports)) {
                let entry = {
                    categoryName: key,
                    categoryEntries: value
                }
                _groupedReportsArray.push(entry)
            }

            setReports(result)
            setGroupedReports(_groupedReportsArray)
        })

    }, [data])

    const getEntriesColumnSum = (entries, columnName) => {
        return entries.reduce((a, b) => a + parseFloat(b[columnName]), 0)
    }

    const getGroupedTotalCompletedItems = (categoryEntries) => {
        var _groupedTotalCompletedItems = categoryEntries.map(x => x.totalCompletedItems.split(' ')[1]).filter((value, index, array) => array.indexOf(value) === index)

        _groupedTotalCompletedItems.forEach((x, index) => {
            let total = 0
            categoryEntries.forEach(y => {
                if (x === y.totalCompletedItems.split(' ')[1])
                    total += parseFloat(y.totalCompletedItems.split(' ')[0])
            })
            _groupedTotalCompletedItems[index] = total + ' ' + _groupedTotalCompletedItems[index]
        });

        return _groupedTotalCompletedItems
    }

    /* Build ranked bar rows from flat report data for the perf cards */
    const rankedBySales = [...reports]
        .sort((a, b) => parseFloat(b.totalSales) - parseFloat(a.totalSales))
        .slice(0, 8)

    const maxSales = rankedBySales.length ? parseFloat(rankedBySales[0].totalSales) : 1

    return (
        <div className="sbr">
            <SetTempEditOrderModal display={editJobModal} toggle={setEditJobModal} jobId={selectedJobId} />

            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('reports.servicesReportsTitle')}</div>
                    <div className="sub">{t('reports.servicesSubtitle')}</div>
                </div>
                <div className="act">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--muted)', fontWeight: 600, cursor: 'pointer' }}>
                        <Form.Check
                            value={isGroupedByCategory}
                            type="checkbox"
                            id="group-by-category-toggle"
                            onChange={() => setIsGroupedByCategory(!isGroupedByCategory)}
                        />
                        {t('reports.groupByCategory')}
                    </label>
                </div>
            </div>

            {/* Filters */}
            <div style={{ marginBottom: '14px' }}>
                <ServicesReportsFilter onFilter={setFilters} />
            </div>

            {/* KPI summary tiles */}
            <div className="kpis3" style={{ marginBottom: '18px' }}>
                <div className="kpi b1">
                    <div className="ico">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 3v4M3 5h4M13 3l2.5 5L21 10l-5.5 2L13 17l-2.5-5L5 10l5.5-2z"/></svg>
                    </div>
                    <div className="l">{t('reports.servicesLabel')}</div>
                    <div className="n num">{reports.length || '—'}</div>
                    <div className="d flat">{t('reports.inSelectedPeriod')}</div>
                </div>
                <div className="kpi b2">
                    <div className="ico">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    </div>
                    <div className="l">{t('reports.totalJobsLabel')}</div>
                    <div className="n num">{reports.reduce((s, r) => s + (parseFloat(r.totalJobs) || 0), 0) || '—'}</div>
                    <div className="d flat">{t('reports.acrossAllServices')}</div>
                </div>
                <div className="kpi lift">
                    <div className="ico" style={{ background: 'rgba(255,255,255,.12)', borderColor: 'rgba(255,255,255,.22)', color: '#fff' }}>
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    </div>
                    <div className="l">{t('reports.totalRevenue')}</div>
                    <div className="n num">{rsdFormat(reports.reduce((s, r) => s + (parseFloat(r.totalSales) || 0), 0)) || '—'}</div>
                    <div className="d flat" style={{ color: '#A9B6CE' }}>{t('reports.allServicesCombined')}</div>
                </div>
            </div>

            <div className="cols">
                {/* Main: ranked by revenue */}
                <div>
                    {reports.length > 0 && (
                        <div className="card-sbr" style={{ marginBottom: '18px' }}>
                            <div className="ch">
                                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--brand)', flexShrink: 0 }}><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
                                <span className="t" style={{ fontWeight: 800, color: 'var(--ink)', fontSize: '14px' }}>{t('reports.topServicesByRevenue')}</span>
                                <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 700, color: 'var(--muted)' }}>{t('reports.thisPeriod')}</span>
                            </div>

                            {rankedBySales.map((r, i) => {
                                const pct = maxSales > 0 ? Math.round((parseFloat(r.totalSales) / maxSales) * 100) : 0
                                const sharePct = reports.reduce((s, x) => s + parseFloat(x.totalSales), 0) > 0
                                    ? Math.round((parseFloat(r.totalSales) / reports.reduce((s, x) => s + parseFloat(x.totalSales), 0)) * 100)
                                    : 0
                                return (
                                    <div key={r.serviceId || i} className="perf" style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/reports/jobs-history/?serviceId=${r.serviceId}`)}>
                                        <span className="rk num">{i + 1}</span>
                                        <div className="info">
                                            <div className="nm">{r.service || r.serviceName}</div>
                                            <div className="bar">
                                                <i style={{ width: `${pct}%` }}></i>
                                            </div>
                                        </div>
                                        <div className="rt">
                                            <div className="v num">{rsdFormat(r.totalSales)}</div>
                                            <div className="vs">{t('reports.revenueShare', { pct: sharePct, count: r.completedJobs })}</div>
                                        </div>
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#C4CCD8', flexShrink: 0, marginLeft: '6px' }}><polyline points="9 18 15 12 9 6"/></svg>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {/* Full data table */}
                    <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                        <div className="ch" style={{ padding: '16px 20px 0' }}>
                            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--brand)', flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            <span className="t" style={{ fontWeight: 800, color: 'var(--ink)', fontSize: '14px' }}>{t('reports.fullData')}</span>
                        </div>

                        <div style={{ padding: '12px 20px 20px', position: 'relative', minHeight: '100px' }}>
                            {reports.length > 0 ? (
                                <>
                                    <Collapse in={!isGroupedByCategory}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <Table responsive hover style={{ borderTop: '2px solid var(--line)', fontSize: '13px' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}></th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.serviceHeader')}</th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.categoryHeader')}</th>
                                                        <th className="text-end" style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.totalJobsHeader')}</th>
                                                        <th className="text-end" style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.completedJobsHeader')}</th>
                                                        <th className="text-end" style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.totalSalesHeader')}</th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.completedItemsHeader')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reports.map((service, i) => (
                                                        <tr key={service.serviceId || i}>
                                                            <td style={{ width: 34 }}>
                                                                <i className="fa-solid fa-eye fa-sm" title={t('reports.viewJobHistory')}
                                                                    style={{ cursor: 'pointer', color: 'var(--brand)' }}
                                                                    onClick={() => navigate(`/reports/jobs-history/?serviceId=${service.serviceId}`)}></i>
                                                            </td>
                                                            <td style={{ fontWeight: 700, color: 'var(--ink)' }}>{service.service || service.serviceName}</td>
                                                            <td style={{ color: 'var(--text)' }}>{service.category}</td>
                                                            <td className="text-end num">{service.totalJobs}</td>
                                                            <td className="text-end num">{service.completedJobs}</td>
                                                            <td className="text-end num" style={{ fontWeight: 800, color: 'var(--ink)' }}>{rsdFormat(service.totalSales)}</td>
                                                            <td className="num" style={{ whiteSpace: 'nowrap' }}>{service.totalCompletedItems}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Collapse>

                                    <Collapse in={isGroupedByCategory}>
                                        <div>
                                            <Table responsive style={{ borderTop: '2px solid var(--line)', fontSize: '13px' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}></th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.categoryHeader')}</th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.serviceHeader')}</th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.totalJobsHeader')}</th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.completedJobsHeader')}</th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.totalSalesHeader')}</th>
                                                        <th style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('reports.totalCompletedItemsHeader')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {groupedReports.map((report, reportIndex) => (
                                                        <tr key={`groupedreport-${reportIndex}`}>
                                                            <td></td>
                                                            <td style={{ fontWeight: 700, color: 'var(--ink)' }}>{report.categoryName}</td>
                                                            <td>
                                                                {report.categoryEntries.map((entry, entryIndex) => (
                                                                    <p key={`report-${reportIndex}-entry-${entryIndex}`} className="mb-0" style={{ fontSize: '13px', color: 'var(--text)' }}>{entry.service}</p>
                                                                ))}
                                                            </td>
                                                            <td className="num">{getEntriesColumnSum(report.categoryEntries, 'totalJobs')}</td>
                                                            <td className="num">{getEntriesColumnSum(report.categoryEntries, 'completedJobs')}</td>
                                                            <td className="num" style={{ fontWeight: 800, color: 'var(--ink)' }}>{rsdFormat(getEntriesColumnSum(report.categoryEntries, 'totalSales'))}</td>
                                                            <td>
                                                                {getGroupedTotalCompletedItems(report.categoryEntries).map((entry, entryIndex) => (
                                                                    <p key={`report-${reportIndex}-entry-${entryIndex}`} className="mb-0 num" style={{ fontSize: '13px' }}>{entry}</p>
                                                                ))}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Collapse>
                                </>
                            ) : (
                                isFetching ?
                                    <SkeletonRows rows={6} widths={['8%', '28%', '16%', '16%', '16%', '16%']} />
                                    :
                                    <div className="sbr-empty">
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 3v4M3 5h4M13 3l2.5 5L21 10l-5.5 2L13 17l-2.5-5L5 10l5.5-2z"/></svg>
                                        {t('reports.noReportsFound')}
                                    </div>
                            )}
                            {reports.length > 0 && isFetching && <OverlayLoader />}
                        </div>
                    </div>
                </div>

                {/* Right rail: category breakdown */}
                {groupedReports.length > 0 && (
                    <div>
                        <div className="card-sbr">
                            <div className="ch">
                                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--brand)', flexShrink: 0 }}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                                <span className="t" style={{ fontWeight: 800, color: 'var(--ink)', fontSize: '14px' }}>{t('reports.byCategory')}</span>
                            </div>
                            {groupedReports.map((report, i) => {
                                const catTotal = getEntriesColumnSum(report.categoryEntries, 'totalSales')
                                const grandTotal = reports.reduce((s, r) => s + parseFloat(r.totalSales), 0)
                                const sharePct = grandTotal > 0 ? Math.round((catTotal / grandTotal) * 100) : 0
                                const maxCat = Math.max(...groupedReports.map(g => getEntriesColumnSum(g.categoryEntries, 'totalSales')), 1)
                                const barPct = Math.round((catTotal / maxCat) * 100)
                                return (
                                    <div key={`cat-${i}`} className="perf">
                                        <span className="rk num">{i + 1}</span>
                                        <div className="info">
                                            <div className="nm">{report.categoryName}</div>
                                            <div className="bar">
                                                <i style={{ width: `${barPct}%` }}></i>
                                            </div>
                                        </div>
                                        <div className="rt">
                                            <div className="v num">{rsdFormat(catTotal)}</div>
                                            <div className="vs">{t('reports.categoryShare', { pct: sharePct, count: getEntriesColumnSum(report.categoryEntries, 'completedJobs') })}</div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServicesReportsPage;
